import React from "react";
import {
  TextField,
  Button,
  Typography,
  Box,
  Radio,
  RadioGroup,
  FormControlLabel,
  Chip,
  IconButton,
  InputAdornment,
  CircularProgress,
  FormHelperText,
  Dialog,
  List,
  ListItem,
  ListItemText,
  Divider
} from "@mui/material";

import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import { useNavigate } from "react-router-dom";
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import Sidebar from "../../../Navigation/Sidebar/Sidebar";
import "./WarehouseRuleCreation.css";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import ErrorIcon from '@mui/icons-material/Error';

const CohortNewRuleCreationComponent = ({
  formData,
  searchInput,
  searchResults,
  searchLoading,
  showDropdown,
  errors,
  isSubmitting,
  ruleTypes,
  onChange,
  onSearchInputChange,
  onAddName,
  onRemoveName,
  onSlotCountChange,
  onSubmit,
  onDateChange,
  showSuccessModal,
  newRuleId,
  setShowSuccessModal,
  newCohortName,
  ruleFailures,
  setShowFailureModal,
  showFailureModal,
  isMvidSearch = false,
  showErrorMsg,
  ruleFailureData,
  onMvidSearch, // 🔥 New prop for MVID search
}) => {
  const navigate = useNavigate();

  const isSaveDisabled = () => {
    if (!formData.ruleType) return true;
    if (formData.ruleType === "OfferPrice") {
      return !formData.maxPrice;
    }
    return formData.names.length === 0 || !formData.validationType;
  };

  // 🔥 Handle Enter key press for search
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && isMvidSearch && searchInput.trim()) {
      onMvidSearch(searchInput);
    }
  };

  return (
    <div className="warehouse-rule-container">
      <Sidebar />

      <div className="warehouse-rule-content">
        <div className="warehouse-rule-header">
          <button onClick={() => navigate(-1)} className="warehouse-rule-back-button">←</button>
          <h2 className="warehouse-rule-title">Create New Rule</h2>
        </div>

        <div className="warehouse-rule-form">
          {/* Start Date */}
          <div className="warehouse-rule-section">
            <Typography className="warehouse-rule-section-title">Select Start Date</Typography>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                value={formData.startDate}
                onChange={onDateChange}
                minDate={new Date()}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    variant="outlined"
                    size="small"
                    className="warehouse-rule-input"
                  />
                )}
              />
            </LocalizationProvider>
          </div>

          <div className="warehouse-rule-divider"></div>

          {/* Rule Type */}
          <div className="warehouse-rule-section">
            <Typography className="warehouse-rule-section-title">Select Rule Type</Typography>
            <RadioGroup
              name="ruleType"
              value={formData.ruleType}
              onChange={onChange}
              className="warehouse-rule-radio-group"
            >
              <div className="warehouse-rule-radio-grid">
                {ruleTypes.map((type) => (
                  <FormControlLabel
                    key={type.value}
                    value={type.value}
                    control={<Radio className="warehouse-rule-radio" />}
                    label={
                      <div className="rule-type-option">
                        <span className="rule-type-icon-container">
                          <img src={type.icon} className="rule-type-icon" alt={type.label} />
                        </span>
                        <span className="rule-type-label">{type.label}</span>
                      </div>
                    }
                    className="warehouse-rule-radio-label"
                  />
                ))}
              </div>
            </RadioGroup>
            {errors.ruleType && (
              <FormHelperText error className="error-message">
                Please select a rule type
              </FormHelperText>
            )}
          </div>

          <div className="warehouse-rule-divider"></div>

          {/* Name or Max Price */}
          <div className="warehouse-rule-section">
            <Typography className="warehouse-rule-section-title">
              {formData.ruleType === "OfferPrice" ? "Max Price" : "Name"}
            </Typography>
            {formData.ruleType === "OfferPrice" ? (
              <>
                <TextField
                  type="text"
                  placeholder="Enter max price"
                  fullWidth
                  variant="outlined"
                  size="small"
                  name="maxPrice"
                  value={formData.maxPrice || ""}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === "" || /^\d{1,4}(\.\d{0,2})?$/.test(val)) {
                      onChange(e);
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">₹</InputAdornment>
                    ),
                    inputProps: {
                      inputMode: "decimal",
                    },
                  }}
                  className="warehouse-rule-input"
                  error={errors.maxPrice}
                />
                {errors.maxPrice && (
                  <FormHelperText error className="error-message">
                    Please enter a max price
                  </FormHelperText>
                )}
              </>
            ) : (
              <>
                <div className="warehouse-rule-search-container">
                  <div className="warehouse-rule-search-wrapper">
                    <TextField
                      value={searchInput}
                      onChange={onSearchInputChange}
                      onKeyPress={handleKeyPress}
                      placeholder={
                        isMvidSearch
                          ? "Search by MVId or Name..."
                          : `Search ${formData.ruleType}...`
                      }
                      fullWidth
                      variant="outlined"
                      size="small"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                          </InputAdornment>
                        ),
                        endAdornment: searchLoading && (
                          <InputAdornment position="end">
                            <CircularProgress size={20} />
                          </InputAdornment>
                        )
                      }}
                      className="warehouse-rule-input warehouse-rule-search-input"
                      disabled={!formData.ruleType}
                    />
                    
                    {/* 🔥 Search Button - Only for MVID */}
                    {isMvidSearch && (
                      <Button
                        variant="contained"
                        className="warehouse-rule-search-button"
                        onClick={() => onMvidSearch(searchInput)}
                        disabled={!searchInput.trim() || searchLoading}
                        startIcon={searchLoading ? <CircularProgress size={16} /> : <SearchIcon />}
                      >
                        {searchLoading ? 'Searching...' : 'Search'}
                      </Button>
                    )}
                  </div>

                  {showDropdown && (
                    <div className="warehouse-rule-dropdown">
                      {searchLoading ? (
                        <div className="warehouse-rule-dropdown-item">
                          <CircularProgress size={20} />
                        </div>
                      ) : searchResults.length > 0 ? (
                        searchResults.map(item => {
                          const itemId = item.id || item.mvId;
                          const itemName = item.variant_name || item.name || 'N/A';
                          
                          return (
                            <div
                              key={itemId}
                              onClick={() => onAddName(item)}
                              className="warehouse-rule-dropdown-item"
                            >
                              {isMvidSearch ? (
                                <div className="mvid-search-result">
                                  <span className="mvid-id">
                                    <strong>MVId:</strong> {itemId}
                                  </span>
                                  <span className="mvid-name">
                                    {itemName}
                                  </span>
                                </div>
                              ) : (
                                <span>
                                  {item.id} <span style={{ color: "#888" }}>({item.name})</span>
                                </span>
                              )}
                            </div>
                          );
                        })
                      ) : (
                        <div className="warehouse-rule-dropdown-no-results">
                          {searchInput ? "No results found" : "Start typing to search"}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className="warehouse-rule-selected-names">
                  {formData.names.map(item => {
                    const itemId = item.id || item.mvId;
                    const itemName = item.name || item.variant_name || 'N/A';
                    
                    return (
                      <Chip
                        key={itemId}
                        label={
                          isMvidSearch
                            ? `MVId: ${itemId} - ${itemName}`
                            : item.name
                        }
                        onDelete={() => onRemoveName(itemId)}
                        deleteIcon={<CloseIcon />}
                        className="warehouse-rule-chip"
                      />
                    );
                  })}
                </div>
                {errors.names && (
                  <FormHelperText error className="error-message">
                    Please select at least one {formData.ruleType.toLowerCase()}
                  </FormHelperText>
                )}
              </>
            )}
          </div>

          {formData.ruleType !== "OfferPrice" && (
            <>
              <div className="warehouse-rule-divider"></div>
              <div className="warehouse-rule-section">
                <Typography className="warehouse-rule-section-title">Select Validation Type</Typography>
                <RadioGroup
                  name="validationType"
                  value={formData.validationType}
                  onChange={onChange}
                  className="warehouse-rule-validation-group"
                >
                  <div className="warehouse-rule-slot-container">
                    <FormControlLabel
                      value="QUANTITY"
                      control={<Radio className="warehouse-rule-radio" />}
                      label="MANDATORY"
                      className="warehouse-rule-radio-label"
                    />

                    <div
                      className="warehouse-rule-slot-controls"
                      style={{
                        opacity: formData.validationType === "RESTRICTED" ? .3 : 1,
                        pointerEvents: formData.validationType === "RESTRICTED" ? "none" : "auto",
                      }}
                    >
                      <IconButton
                        onClick={() => onSlotCountChange(-1)}
                        disabled={formData.slotCount <= 1}
                        className="warehouse-rule-slot-button"
                      >
                        <RemoveIcon />
                      </IconButton>

                      <Typography
                        className="warehouse-rule-slot-value"
                        style={{
                          color: formData.validationType === "RESTRICTED" ? "black" : "#fff",
                        }}
                      >
                        {formData.slotCount}
                      </Typography>

                      <IconButton
                        onClick={() => onSlotCountChange(1)}
                        className="warehouse-rule-slot-button"
                      >
                        <AddIcon />
                      </IconButton>
                    </div>
                  </div>

                  <Typography className="warehouse-rule-hint-text">
                    In case of adding multiple selections in MANDATORY, Rule will check validation for ANY 1 selection
                  </Typography>

                  <div className="warehouse-rule-slot-container">
                    <FormControlLabel
                      value="RESTRICTED"
                      control={<Radio className="warehouse-rule-radio" />}
                      label="RESTRICTED"
                      className="warehouse-rule-radio-label"
                    />
                  </div>
                  <Typography className="warehouse-rule-hint-text">
                    In case of adding multiple selections in RESTRICTED, Rule will check validation For ALL selections
                  </Typography>
                </RadioGroup>
                {errors.validationType && (
                  <FormHelperText error className="error-message">
                    Please select a validation type
                  </FormHelperText>
                )}
              </div>
            </>
          )}

          {/* Success Dialog */}
          <Dialog
            open={showSuccessModal}
            onClose={() => {
              setShowSuccessModal(false);
              navigate(-1);
            }}
            PaperProps={{
              style: { borderRadius: 20, padding: '20px 30px', textAlign: 'center' }
            }}
          >
            <CheckCircleIcon style={{ fontSize: 60, color: "#00b300", margin: '10px auto' }} />
            <Typography variant="h6" gutterBottom>
              Successfully
            </Typography>
            <Typography variant="body1">
              Rule Created Successfully (ID <strong>{newRuleId}</strong>)
            </Typography>
            <Typography variant="body1" sx={{ mt: 1 }}>
              For <strong>{newCohortName}</strong>
            </Typography>
            <Button
              variant="contained"
              sx={{ mt: 3, backgroundColor: '#00b300', '&:hover': { backgroundColor: '#009900' } }}
              onClick={() => {
                setShowSuccessModal(false);
                navigate(-1);
              }}
            >
              Ok
            </Button>
          </Dialog>

          {/* Failure Dialog */}
          <Dialog
            open={showFailureModal}
            onClose={() => setShowFailureModal(false)}
            fullWidth
            maxWidth="sm"
            PaperProps={{
              style: { borderRadius: 20, padding: '20px' }
            }}
          >
            <Box display="flex" flexDirection="column" alignItems="center" p={2}>
              <ErrorIcon style={{ fontSize: 60, color: "#f44336", marginBottom: 16 }} />
              <Typography variant="h6" gutterBottom color="error">
                Rule Creation Failed
              </Typography>

              {ruleFailureData && ruleFailureData.length > 0 && (
                <Box width="100%" mt={2}>
                  <List dense>
                    {ruleFailures.map((failure, index) => (
                      <React.Fragment key={index}>
                        <ListItem>
                          <ListItemText
                            primary={
                              <Typography variant="body1" color="error">
                                {failure.failureType.replace(/_/g, ' ')}
                              </Typography>
                            }
                            secondary={
                              failure.failedItems.ids.length > 0 ? (
                                <Typography variant="body2">
                                  IDs: {failure.failedItems.ids.join(', ')}
                                </Typography>
                              ) : (
                                <Typography variant="body2">
                                  {failure.failedItems.showErrorMsg}
                                </Typography>
                              )
                            }
                          />
                        </ListItem>
                        {index < ruleFailures.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                </Box>
              )}

              <Button
                variant="contained"
                color="error"
                sx={{ mt: 3 }}
                onClick={() => setShowFailureModal(false)}
              >
                Close
              </Button>
            </Box>
          </Dialog>

          {/* Action Buttons */}
          <div className="warehouse-rule-actions">
            <Button
              variant="outlined"
              onClick={() => navigate(-1)}
              className="warehouse-rule-cancel-button"
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={onSubmit}
              className={`warehouse-rule-save-button ${isSaveDisabled() ? "disabled" : "enabled"}`}
              disabled={isSaveDisabled() || isSubmitting}
            >
              Save
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CohortNewRuleCreationComponent;